import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router'

import lang from '@/lang'

import { Title, SubTitle } from '@/modules/base/components/texts'
import { TextInput } from '@/modules/base/components/inputs'
import MagnifierIcon from '@assets/images/icons/search.svg'
import PlusIcon from '@assets/images/apps/plus.svg'
import HeaderBackground from '@assets/images/apps/header.png'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import Loader from '@/modules/base/components/loader/loader-inline'
import UnlockView from '@/modules/base/components/unlock-view/unlock-view'
import { SUBSCRIPTION_MODULES as subscriptionModules } from '@/modules/base/util/constants'


import PublisherForm from './publisher-form'
import FeatureLockedNotice from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-locked-notice'
import { FeatureTypes } from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-config'
import AppCard from './card/app'
import OwnAppCard from './card/own'
import Card from './card/index'

import IconCheck from '@assets/images/icons/check.svg'
import EmptyIcon from '@assets/images/emptyStates/marketplace.svg'
import IconPlus from '@assets/images/icons/add.svg'

import {
  fetchPublisher,
  fetchApps,
  putPublisher,
  setPath,
  fetchPublishedApps,
  fetchDevelopers,
  setSearchText,
  checkResults,
} from '@/redux/ducks/apps'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'

export const PATH_PUBLISHER = 'publisher'
export const PATH_PUBLISHED = 'published'
export const PATH_OWN = 'own'
export const PATH_POPULAR = 'popular'
export const PATH_NEW = 'new'
export const PATH_FEATURED = 'featured'
export const PATH_ALL = 'all'

const ALLOWED_FILTERS = [PATH_OWN, PATH_NEW, PATH_POPULAR, PATH_FEATURED, PATH_PUBLISHED, PATH_PUBLISHER, PATH_ALL]

const useStyles = makeStyles(theme => ({
  root: {
    height: `calc(100vh - ${theme.menuHeight}px)`,
    overflow: 'auto',
  },
  header: {
    height: 231,
    background: `url(${HeaderBackground})`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundSize: 'cover',
    alignItems: 'center',
    padding: 50,
  },
  title: {
    color: '#fff',
  },
  subtitle: {
    marginBottom: 20,
    fontSize: 17,
  },
  search: {
    width: 435,
    '& svg': {
      fill: theme.iconFillColor,
    },
  },
  content: {
    padding: '40px 35px 0',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: theme.backgroundColor,
    minHeight: '100%',
  },
  menuWrapper: {
    marginRight: 30,
    width: 230,
  },
  menu: {
    marginBottom: 20,
  },
  devMenu: {
    paddingTop: 30,
    borderTop: `1px solid ${theme.borderColor}`,
  },
  menuItem: ({ active }) => {
    const styles = {
      padding: '0 5px',
      '& a': {
        display: 'block',
        color: 'inherit',
      },
      marginBottom: 30,
    }

    const activeStyle = { ...styles, borderLeft: `2px solid ${theme.primary}`, fontWeight: 'bold' }

    styles['&:hover'] = activeStyle

    return active ? activeStyle : styles
  },
  appsContainer: {
    marginLeft: 30,
    width: '100%',
  },
  headline: {
    fontSize: 19,
    lineHeight: '23px',
    letterSpacing: '0.38px',
    marginBottom: 20,
  },
  appsList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginLeft: -10,
  },
  loader: {
    width: '100%',
    height: 200,
  },
  add: {
    '& a': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      borderStyle: 'dashed',
      color: theme.textColor,
      fontSize: 16,
      '& svg': {
        fill: theme.primary,
        marginBottom: 10,
      },
      '&:hover': {
        color: theme.primary,
      },
    },
  },
  carousel: {
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    '&:after': {
      content: '""',
      display: 'block',
      paddingBottom: '100%',
    },
  },
  slider: ({ slides, slide }) => ({
    position: 'absolute',
    left: `-${slide * 100}%`,
    top: 0,
    width: `${slides * 100}%`,
    // height: '100%',
    transition: 'left .5s',
    display: 'flex',
  }),
  slide: ({ slides }) => ({
    width: `${100 / slides}%`,
    // height: '100%',
    position: 'relative',
  }),
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 30,
  },
  infoNumber: {
    minWidth: 36,
    height: 36,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.primarySuperLight,
    marginRight: 20,
    borderRadius: theme.borderRadius,
    color: '#fff',
  },
  infoHeadline: {
    fontSize: 17,
    lineHeight: '26px',
    margin: 0,
    '& + p': {
      margin: 0,
    },
  },
  emptyState: {
    padding: '50px 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    '& p': {
      fontSize: 19,
      lineHeight: '23px',
    },
  },
}))

const Header = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { path, searchText } = useSelector(state => state.apps)

  useEffect(() => {
    if (path === PATH_PUBLISHED) dispatch(fetchPublishedApps())
    else if (path !== PATH_PUBLISHER) dispatch(fetchApps({ path, searchText }))
  }, [path, searchText])

  return (
    <div className={classes.header}>
      <Title size="large" position="center" className={classes.title}>{lang.marketplace.headline(lang.productName)}</Title>
      <SubTitle position="center" className={clsx(classes.title, classes.subtitle)}>{lang.marketplace.subtitle(lang.productName)}</SubTitle>
      <TextInput
        className={classes.search}
        icon={MagnifierIcon}
        iconPosition="before"
        placeholder={lang.marketplace.search}
        onChange={searchText => dispatch(setSearchText({ searchText }))}
      />
    </div>
  )
}

const MenuItem = ({ children, type }) => {
  const { path } = useSelector(state => state.apps)
  const dispatch = useDispatch()
  const classes = useStyles({ active: type === path })

  const onClick = () => {
    dispatch(setPath(type))
    window.history.pushState(null, null, `${location.origin}/apps/${type}`)
  }

  return <li className={classes.menuItem}><a onClick={onClick}>{children || lang.marketplace.menuitems[type]}</a></li>
}

const Menu = () => {
  const classes = useStyles()

  const { isPublisher } = useSelector(state => state.apps)
  return (
    <div className={classes.menuWrapper}>
      <ul className={classes.menu}>
        <MenuItem type={PATH_OWN} />
        <MenuItem type={PATH_POPULAR} />
        <MenuItem type={PATH_NEW} />
        <MenuItem type={PATH_ALL} />
      </ul>
      <ul className={classes.devMenu}>
        <MenuItem type={PATH_PUBLISHED}>{lang.marketplace.menuitems.published(isPublisher)}</MenuItem>
        {isPublisher && (
          <MenuItem type={PATH_PUBLISHER} />
        )}
      </ul>
    </div>
  )
}

const Publisher = () => {
  const dispatch = useDispatch()
  const { publisher } = useSelector(state => state.apps)

  return (
    <PublisherForm data={publisher} onSubmit={data => dispatch(putPublisher(data))} />
  )
}

const PublisherInfo = ({ onClick }) => {
  const classes = useStyles()
  const publisherinfo = lang.marketplace.publisherinfo

  return (
    <div>
      {publisherinfo.workflow.map((step, index) => (
        <div key={index} className={classes.infoRow}>
          <div className={classes.infoNumber}>{index + 1}</div>
          <div>
            <p className={classes.infoHeadline}><strong>{step.title}</strong></p>
            <p dangerouslySetInnerHTML={{ __html: step.text(lang.productName) }} />
          </div>
        </div>
      ))}

      <div>
        <ButtonIconSquare
          type="button"
          icon={IconCheck}
          label={publisherinfo.button}
          onClick={onClick}
        />
      </div>
    </div>
  )
}

const RegisterPublisher = () => {
  const [slide, setSlide] = useState(0)
  const classes = useStyles({ slides: 2, slide }, [slide])

  return (
    <div className={classes.carousel}>
      <div className={classes.slider}>
        <div className={classes.slide}><PublisherInfo onClick={() => setSlide(slide + 1)} /></div>
        <div className={classes.slide}><Publisher /></div>
      </div>
    </div>
  )
}

const EmptyState = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const onClick = () => {
    dispatch(setPath(PATH_POPULAR))
    window.history.pushState(null, null, `${location.origin}/apps/${PATH_POPULAR}`)
  }

  const empty = lang.marketplace.empty

  return (
    <div className={classes.emptyState}>
      <SVGIcon svg={EmptyIcon} width="100px" height="100px" />
      <p>{empty.text}</p>
      <ButtonIconSquare
        type="button"
        icon={IconPlus}
        label={empty.button}
        onClick={onClick}
      />
    </div>
  )
}

const Content = () => {
  const classes = useStyles()

  const { isPublisher, apps, path } = useSelector(state => state.apps)
  const { loading: accessLoading, hasPublicApiAccess } = useSelector(state => state.subscription.details)

  const accessLoader = <div className={classes.loader}><Loader /></div>

  switch (path) {
    case PATH_PUBLISHER:
      if (accessLoading) return accessLoader

      if (!hasPublicApiAccess) {
        return <FeatureLockedNotice featureType={FeatureTypes.PUBLIC_API} description={lang.marketplace.publicApi.textReadOnly} />
      }

      return <Publisher />

    case PATH_PUBLISHED:
      if (accessLoading) return accessLoader

      if (!isPublisher) {
        return hasPublicApiAccess ? <RegisterPublisher /> : <FeatureLockedNotice featureType={FeatureTypes.PUBLIC_API} />
      }

      return (
        <>
          {!hasPublicApiAccess && (
            <FeatureLockedNotice
              featureType={FeatureTypes.PUBLIC_API}
              description={apps[path]?.length ? lang.marketplace.publicApi.textReadOnly : undefined}
            />
          )}
          <div className={classes.appsList}>
            {hasPublicApiAccess && (
              <Card
                href={'/apps/new'}
                className={clsx(classes.app, classes.add)}
              >
                <SVGIcon width="50px" height="50px" svg={PlusIcon} />
                <span>Erweiterung erstellen</span>
              </Card>
            )}
            {apps[path] && apps[path].map((app, idx) => (
              <OwnAppCard
                key={`app_${app.appId}_${app.version}`}
                href={`/app/edit/${app.appId}/${app.version}`}
                {...app}
              />
            ))}
          </div>
        </>
      )

    default:
      return (
        <div className={classes.appsList}>
          {apps[path] === null && (
            <div className={classes.loader}><Loader /></div>
          )}
          {apps[path] && !apps[path].length && path === PATH_OWN && (
            <EmptyState />
          )}
          {apps[path] && apps[path].map(app => (
            <AppCard
              key={`app_${app.appId}_${app.version}`}
              href={`/app/${path}/show/${app.appId}`}
              {...app}
            />
          ))}
        </div>
      )
  }
}

const AppsList = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const openUpgradeModal = useUpgradeModal()

  const { filter: routeFilter } = useParams()
  const filter = ALLOWED_FILTERS.includes(routeFilter) ? routeFilter : props.filter

  const { isPublisher, path } = useSelector(state => state.apps)
  const allowed = useCheckModuleAccess(subscriptionModules.MARKETPLACE)
  const subscriptionsLoading = useSelector(state => state.subscription.details).loading

  useEffect(() => {
    dispatch(fetchPublisher())
    dispatch(fetchDevelopers())

    if (filter === PATH_OWN) dispatch(checkResults(filter, PATH_POPULAR))
    else dispatch(setPath(filter))
  }, [])

  const upgrade = () => {
    openUpgradeModal()
  }

  if (!allowed && !subscriptionsLoading) {
    return (
      <UnlockView
        title={lang.menu.apps.title}
        description={lang.menu.apps.upgradeText}
        onUpgradeClick={upgrade}
      >
        <Loader visible={true} text={lang.checkingPermission} />
      </UnlockView>
    )
  }

  return (
    <div className={classes.root}>
      <Header />
      <div className={classes.content}>
        <Menu />
        <div className={classes.appsContainer}>
          <div className={classes.headline}>{lang.marketplace.headlines[path]?.(lang.productName, isPublisher)}</div>
          <Content />
        </div>
      </div>
    </div>
  )
}

AppsList.propTypes = {
  filter: PropTypes.oneOf([
    PATH_OWN,
    PATH_NEW,
    PATH_POPULAR,
    PATH_FEATURED,
    PATH_PUBLISHED,
    PATH_PUBLISHER,
    PATH_ALL,
  ]),
}

AppsList.defaultProps = {
  filter: PATH_OWN,
}

export default AppsList
